<template>
  <div class="member-info-page">
    <!-- 顶部提示 -->
    <div class="top-notice">
      <span class="notice-text">完善会员信息，即可享受更多权益</span>
    </div>

    <!-- 审核状态提示 -->
    <el-alert v-if="auditStatus !== null"
      :type="auditStatus === 1 ? 'success' : auditStatus === 2 ? 'warning' : 'info'" 
      :closable="false"
      class="audit-status-alert">
      <template #title>
        <span>审核状态：{{ auditStatusText }}</span>
        <span v-if="auditStatus === 2 && memberInfo.audit_remark" class="audit-remark">
          （驳回原因：{{ memberInfo.audit_remark }}）
        </span>
      </template>
    </el-alert>

    <!-- 使用弹窗组件作为页面内容 -->
    <div class="form-container">
      <MemberInfoDialog 
        v-model="dialogVisible" 
        :initial-data="initialData"
        @submit="handleSubmit" 
      />
      
      <!-- 如果弹窗未打开，显示打开按钮 -->
      <div v-if="!dialogVisible" class="open-form-btn">
        <el-button type="primary" size="large" @click="openForm">
          {{ canEdit ? '完善会员信息' : '查看会员信息' }}
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import api from '@/api/index'
import MemberInfoDialog from '@/components/MemberInfoDialog.vue'
import { MEMBER_TYPES, AUDIT_STATUS_NAMES } from '@/utils/constants'

export default {
  name: 'MemberInfo',

  components: { MemberInfoDialog },

  data() {
    return {
      MEMBER_TYPES,
      dialogVisible: true, // 默认打开
      initialData: {}
    }
  },

  computed: {
    ...mapGetters('auth', ['memberInfo', 'memberType', 'auditStatus']),

    isIndividual() {
      return this.memberType === MEMBER_TYPES.INDIVIDUAL
    },

    isOrganization() {
      return this.memberType === MEMBER_TYPES.ORGANIZATION
    },

    isDirector() {
      return this.memberType === MEMBER_TYPES.DIRECTOR
    },

    auditStatusText() {
      return AUDIT_STATUS_NAMES[this.auditStatus] || '未提交'
    },

    canEdit() {
      // 待审核或已驳回可以编辑
      return this.auditStatus === 0 || this.auditStatus === 2
    }
  },

  mounted() {
    this.loadMemberInfo()
  },

  methods: {
    ...mapActions('auth', ['refreshMemberInfo']),

    /**
     * 加载会员信息
     */
    async loadMemberInfo() {
      if (!this.memberInfo || !this.memberInfo.id) {
        return
      }

      try {
        const res = await api.info()

        if (res && res.data) {
          const data = res.data
          this.initialData = {
            realName: data.name || '',
            phone: data.phone || '',
            email: data.email || '',
            birthday: data.birthday || '',
            company: data.company || '',
            title: data.title || '',
            education: data.education || '本科',
            city: data.city || '',
            degreeNo: data.degree_no || '',
            graduateSchool: data.graduate_school || '',
            major: data.major || '',
            startDate: data.start_date || '',
            experiences: data.experiences ? 
              (typeof data.experiences === 'string' ? 
                JSON.parse(data.experiences) : 
                data.experiences) : 
              [
                { time: [], location: '', supervisorName: '', supervisorHours: '' },
                { time: [], location: '', supervisorName: '', supervisorHours: '' }
              ],
            isMember: data.is_member || '否',
            hasViolation: data.has_violation || '否',
            serviceCount: data.service_count || '',
            caseCount: data.case_count || '',
            remark: data.remark || '',
            photoUrl: data.photo || '',
            certificateUrl: data.certificate || '',
            degreeUrl: data.degree_certificate || ''
          }
        }
      } catch (error) {
        console.error('加载会员信息失败:', error)
      }
    },

    openForm() {
      this.dialogVisible = true
    },

    // 处理表单提交
    async handleSubmit(submitData) {
      try {
        const res = await api.update(submitData)

        if (res && res.code === 2000) {
          this.$message.success(res.msg || '提交成功！')
          this.dialogVisible = false

          // 标记会员信息已完善
          localStorage.setItem('member_info_completed', 'true')

          // 刷新会员信息
          if (res.data) {
            await this.refreshMemberInfo(res.data)
          }

          // 检查是否有待支付的会员类型
          const pendingMemberType = localStorage.getItem('pending_member_type')
          const pendingMemberLevel = localStorage.getItem('pending_member_level')
          
          if (pendingMemberType) {
            // 有待支付的会员类型，弹窗提示用户去支付
            this.$confirm('会员信息已完善，请完成支付以激活会员权限', '提示', {
              confirmButtonText: '去支付',
              cancelButtonText: '稍后再说',
              type: 'success'
            }).then(() => {
              // 跳转到支付页面
              this.$router.push({
                path: '/user/member-payment',
                query: { 
                  type: pendingMemberType, 
                  level: pendingMemberLevel 
                }
              })
            }).catch(() => {
              // 用户选择稍后支付，跳转到首页
              this.$router.push('/user/home')
            })
          } else {
            // 没有待支付的会员类型，直接跳转到首页
            this.$router.push('/user/home')
          }
        }
      } catch (error) {
        console.error('提交失败:', error)
        this.$message.error('提交失败，请稍后重试')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.member-info-page {
  background: #fff;
  min-height: 100vh;
  padding-bottom: 40px;
}

.top-notice {
  padding: 20px 40px;
  border-bottom: 1px solid #eee;

  .notice-text {
    font-size: 16px;
    color: #333;

    &::before {
      content: '';
      display: inline-block;
      width: 4px;
      height: 16px;
      background: #3eaef4;
      margin-right: 10px;
      vertical-align: middle;
    }
  }
}

.audit-status-alert {
  margin: 20px 40px;

  .audit-remark {
    margin-left: 10px;
    color: #e6a23c;
  }
}

.form-container {
  padding: 30px 60px;
  max-width: 1400px;
  margin: 0 auto;
}

.open-form-btn {
  text-align: center;
  padding: 60px 0;

  :deep(.el-button) {
    width: 240px;
    height: 52px;
    font-size: 16px;
    background: #3eaef4;
    border-color: #3eaef4;
    border-radius: 26px;

    &:hover {
      background: #5bbef7;
      border-color: #5bbef7;
    }
  }
}
</style>
